import React, { useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "../ItemTypes";
import { Icon } from "semantic-ui-react";
import styles from "./SectionCard.css";
import ConfirmationModal from "components/ConfirmationModal";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { useDictionary } from "hooks";

const baseDictionary = {
	inputLabel: "Section name",
	removeSectionButton: "Delete section",
	addSectionButton: "Add section",
	confirmModal: {
		title: "Deleting",
		message: "Section, and all its questions and display rules, will be deleted permanently. Are you sure you want to delete it?",
		cancelButtonLabel: "Cancel",
		confirmButtonLabel: "Delete section"
	},
};

export const SectionsCard = ({ id, text, index, moveCard, size, addCard, removeCard, cardTextOnChange }) => {
	const dictionary = useDictionary("simplifiedBuilder.components.manageSectionsModalSectionCard");

	const [ showDeleteModal, setShowDeleteModal ] = useState(false);
	const ref = useRef(null);
	const [, drop ] = useDrop({
		accept: ItemTypes.SECTIONS_CARD,
		hover (item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			// Time to actually perform the action
			moveCard(dragIndex, hoverIndex);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});
	const [ { isDragging }, drag, preview ] = useDrag({
		item: { type: ItemTypes.SECTIONS_CARD, id, index },
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const showModal = () => {
		setShowDeleteModal(true);
	};
	const removeFormSection = () => {
		removeCard(index);
		setShowDeleteModal(false);
	};

	drop(ref);

	return (
		<div ref={ref}>
			<div ref={preview} className={`${styles.sectionCardContainer} ${isDragging ? styles.dragging : ""}`}>
				<div ref={drag} className={styles.move}>
					<Icon name="ellipsis vertical" />
				</div>
				<div className={`ui input ${styles.input}`}>
					<input
						aria-label={dictionary.inputLabel || baseDictionary.inputLabel}
						type="text"
						value={text}
						onChange={event => cardTextOnChange(event.target.value, index)}
					/>
				</div>
				{size > 1 && (
					<IconButton
						className={`${styles.remove} ${styles.pointer}`}
						onClick={showModal}
						icon="trash"
						aria-label={dictionary.removeSectionButton || baseDictionary.removeSectionButton}
						tertiary
					/>
				)}
				{index === size - 1 && (
					<IconButton
						className={`${styles.add} ${styles.pointer}`}
						onClick={addCard}
						icon="plus"
						aria-label={dictionary.addSectionButton || baseDictionary.addSectionButton}
						tertiary
					/>
				)}
			</div>
			{showDeleteModal && (
				<ConfirmationModal
					cancelButtonLabel={dictionary.confirmModal.cancelButtonLabel || baseDictionary.confirmModal.cancelButtonLabel}
					confirmButtonLabel={dictionary.confirmModal.confirmButtonLabel || baseDictionary.confirmModal.confirmButtonLabel}
					confirmButtonOptions={{ danger: true }}
					icon="trash"
					message={`${text} ${dictionary.confirmModal.message || baseDictionary.confirmModal.message}`}
					onCancel={() => setShowDeleteModal(false)}
					title={`${dictionary.confirmModal.title || baseDictionary.confirmModal.title} ${text}`}
					visible={true}
					onConfirm={removeFormSection}
				/>
			)}
		</div>
	);
};
